import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Popover, Select, MenuItem, FormControl } from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import EuroSymbolIcon from '@mui/icons-material/EuroSymbol';
import NearMeIcon from '@mui/icons-material/NearMe';
import dayjs, { Dayjs } from 'dayjs';
import { useVehicleContext } from '../../context/vehicleContext';
import MetricCard from './components/MetricCard';
import LoadForecast from './components/LoadForecast';
import AveragePriceChart from './components/AveragePriceChart';
import EuropeMap from './components/EuropeMap';
import DateTimeInput from '../../components/inputs/DateTimeInput';
import { useTranslation } from 'react-i18next';
import TopPerformingManagers from './components/TopPerformingManagers';
import { apiClient, handleApiError } from '../../services/apiClient';
import { buildApiUrl, API_ENDPOINTS } from '../../config/apiEndpoints';

const DAYS_MAP: Record<string, number> = {
  '7': 6,
  '14': 13,
  '30': 29,
  '60': 59,
  '90': 89,
};

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { vehicles, fetchVehicles } = useVehicleContext();

  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(29, 'day').startOf('day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('day'));
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [quickSelect, setQuickSelect] = useState<string>('30');
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [eurPerKmPeriod, setEurPerKmPeriod] = useState<'week' | 'month' | 'year'>('week');
  const [approachKmPeriod, setApproachKmPeriod] = useState<'week' | 'month' | 'year'>('week');
  const datePickerAnchorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (vehicles.length === 0) {
      fetchVehicles();
    }
  }, [vehicles.length, fetchVehicles]);

  // Fetch dashboard stats
  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const daysDiff = endDate?.diff(startDate, 'day') || 0;
        const daysParam = Math.min(daysDiff + 1, 60); // API max is 60 days

        const url = buildApiUrl(API_ENDPOINTS.stats.dashboard);
        const params = new URLSearchParams();
        params.append('days', daysParam.toString());
        const response = await apiClient(url + '?' + params, { method: 'GET' });
        const data = await response.json();

        // The endpoint returns stats directly, not nested under result
        const stats = data.stats || data.result?.stats;
        if (stats) {
          setDashboardStats(stats);
        }
      } catch (error) {
        handleApiError(error, t('Failed to fetch dashboard stats'));
        setDashboardStats(null);
      }
    };

    if (startDate && endDate) {
      fetchDashboardStats();
    }
  }, [startDate, endDate, t]);

  const handleQuickSelectChange = (value: string) => {
    if (!DAYS_MAP[value]) return;

    setQuickSelect(value);
    const today = dayjs().endOf('day');
    const daysToSubtract = DAYS_MAP[value];
    setStartDate(today.subtract(daysToSubtract, 'day').startOf('day'));
    setEndDate(today);
  };

  const metricCards = useMemo<any[]>(() => {
    const statConfig: Record<string, { icon: JSX.Element; link: string }> = {
      trucks: { icon: <LocalShippingIcon />, link: '/monitoring' },
      negos: { icon: <AutorenewIcon />, link: '/loadSearch' },
      active_trips: { icon: <CalendarTodayIcon />, link: '/monitoring' },
      upcoming_trips: { icon: <CalendarTodayIcon />, link: '/monitoring' },
      eur_per_km: { icon: <EuroSymbolIcon />, link: '/booking' },
      approach_km: { icon: <NearMeIcon />, link: '/booking' },
    };

    const titleLabels: Record<string, string> = {
      trucks: t('Active Trucks'),
      negos: t('Loads in Negotiation'),
      active_trips: t('Active Trips'),
      upcoming_trips: t('Upcoming Trips'),
      eur_per_km: t('EUR per KM'),
      approach_km: t('Approach KM'),
    };

    if (!dashboardStats || dashboardStats.length === 0) {
      return [];
    }

    return dashboardStats.map((stat: any) => {
      const config = statConfig[stat.title] || { icon: <LocalShippingIcon />, link: '/dashboard' };
      const displayTitle = titleLabels[stat.title] || stat.title;

      if (stat.title === 'eur_per_km') {
        const period = stat.periods?.[eurPerKmPeriod];
        return {
          title: displayTitle,
          value: period?.value !== null && period?.value !== undefined
            ? Number(period.value).toFixed(2) : '—',
          icon: config.icon,
          link: config.link,
          percentage: period?.percentage ?? undefined,
          periodSelector: { active: eurPerKmPeriod, onChange: setEurPerKmPeriod },
        };
      }

      if (stat.title === 'approach_km') {
        const period = stat.periods?.[approachKmPeriod];
        return {
          title: displayTitle,
          value: period?.value !== null && period?.value !== undefined
            ? Number(period.value).toFixed(1) + ' km' : '—',
          icon: config.icon,
          link: config.link,
          percentage: period?.percentage ?? undefined,
          periodSelector: { active: approachKmPeriod, onChange: setApproachKmPeriod },
        };
      }

      return {
        title: displayTitle,
        value: stat.value !== null && stat.value !== undefined
          ? stat.value.toString() : '—',
        icon: config.icon,
        link: config.link,
        percentage: stat.percentage ?? undefined,
      };
    });
  }, [dashboardStats, t, eurPerKmPeriod, approachKmPeriod]);

  return (
    <>
      <Box sx={styles.header}>
        {/* Left side: Metric Cards */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: { xs: 1.5, sm: 2 },
          alignItems: 'flex-start',
          flex: 1,
        }}>
          {metricCards.map((card, index) => (
            <Box
              key={index}
              sx={{
                width: { xs: '100%', sm: '180px', md: '200px', lg: '220px' },
                flexShrink: 0,
              }}
            >
              <MetricCard
                title={card.title}
                value={card.value}
                icon={card.icon}
                link={card.link}
                percentage={card.percentage}
                {...(card.periodSelector ? { periodSelector: card.periodSelector } : {})}
              />
            </Box>
          ))}
        </Box>

        {/* Right side: Date Range Picker */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexShrink: 0, flexWrap: 'wrap', width: { xs: '100%', lg: 'auto' } }}>
          <Box
            ref={datePickerAnchorRef}
            onClick={() => setDatePickerOpen(true)}
            sx={styles.dateRangeBox}
          >
            <CalendarTodayIcon sx={{ fontSize: 18, color: 'text.light', mr: 1 }} />
            <Typography sx={{ fontSize: '0.875rem', color: 'text.light', mr: 1 }}>
              {t('Time period:')}
            </Typography>
            <Typography sx={{
              fontSize: '0.875rem',
              color: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.text.white : theme.palette.text.purple,
              fontWeight: 500
            }}>
              {startDate && endDate
                ? `${startDate.format('MMMM D')} - ${endDate.format('MMMM D, YYYY')}`
                : t('Select date range')}
            </Typography>
            <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'text.light', ml: 'auto' }} />
          </Box>

          <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 150 }, flex: { xs: 1, sm: 'initial' } }}>
            <Select
              value={quickSelect}
              onChange={(e) => handleQuickSelectChange(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: 'background.inputGrey',
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '& .MuiSelect-select': {
                  py: 1.25,
                  fontSize: '0.875rem',
                  color: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.text.white : theme.palette.text.purple,
                },
              }}
            >
              <MenuItem value="" disabled>
                <em>{t('Quick select')}</em>
              </MenuItem>
              <MenuItem value="7">{t('Last 7 days')}</MenuItem>
              <MenuItem value="14">{t('Last 14 days')}</MenuItem>
              <MenuItem value="30">{t('Last 30 days')}</MenuItem>
              <MenuItem value="60">{t('Last 60 days')}</MenuItem>
              <MenuItem value="90">{t('Last 90 days')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Popover
          open={datePickerOpen}
          anchorEl={datePickerAnchorRef.current}
          onClose={() => setDatePickerOpen(false)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              p: 2,
              mt: 1,
              borderRadius: 2,
              boxShadow: (theme: any) => `0 4px 12px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.15)'}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DateTimeInput
              value={startDate}
              onChange={(newValue) => {
                setStartDate(newValue as Dayjs | null);
                setQuickSelect('');
              }}
              type="date"
              label={t('Start Date')}
              size="small"
              sx={{ width: 140 }}
            />
            <Typography sx={{ color: 'text.grey' }}>-</Typography>
            <DateTimeInput
              value={endDate}
              onChange={(newValue) => {
                setEndDate(newValue as Dayjs | null);
                setQuickSelect('');
              }}
              type="date"
              label={t('End Date')}
              size="small"
              sx={{ width: 140 }}
            />
          </Box>
        </Popover>
      </Box>

      <Box sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 }, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 1.5, md: 2 }, alignItems: 'stretch' }}>
        <Box sx={{ width: { xs: '100%', lg: '60%' }, flex: { lg: '0 0 70%' }, minWidth: 0 }}>
          <AveragePriceChart startDate={startDate} endDate={endDate} />
        </Box>
        <Box sx={{ width: { xs: '100%', lg: '30%' }, flex: { lg: '0 0 30%' }, minWidth: 0 }}>
          <TopPerformingManagers />
        </Box>
      </Box>

      <Box sx={{ mt: 2, display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 1.5, md: 2 }, alignItems: 'stretch', height: { xs: 'auto', sm: '200px', md: '300px', lg: '500px' }, minHeight: 0, flexShrink: 0 }}>
        <LoadForecast />
        <EuropeMap />
      </Box>

    </>
  );
};

export default Dashboard;

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    mb: { xs: 2.5, sm: 3, md: 3.5 },
    flexDirection: { xs: 'column', lg: 'row' },
    gap: { xs: 2, sm: 2.5, md: 3 },
    flexWrap: 'wrap',
  },
  dateRangeBox: {
    display: 'flex',
    alignItems: 'center',
    px: { xs: 1.5, sm: 2.5 },
    py: { xs: 1.25, sm: 1.5 },
    backgroundColor: 'background.inputGrey',
    borderRadius: 2.5,
    border: '1.5px solid',
    borderColor: 'border.light',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    minWidth: { xs: 'auto', sm: 280 },
    flex: { xs: 1, sm: 'initial' },
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    '&:hover': {
      backgroundColor: 'background.white',
      borderColor: 'border.medium',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      transform: 'translateY(-1px)',
    },
  },
};
